import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { PlatformGeocodingService } from '@data/_generated-api/platform-api/services/platform-geocoding.service';
import { PlatformCityResponse } from '@data/_generated-api/platform-api/models/platform-city-response';
import { PlatformCountryResponse } from '@data/_generated-api/platform-api/models/platform-country-response';
import { HdBirthLocation } from '../hd-chart-library.models';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';

const CITY_DEBOUNCE_MS = 250;

@Component({
  selector: 'hd-location-picker',
  standalone: true,
  imports: [SharedModule, NgSelectModule, TranslocoModule],
  templateUrl: './hd-location-picker.component.html',
  styleUrl: './hd-location-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocationPickerComponent {
  private readonly geocoding = inject(PlatformGeocodingService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  /** Optionele beginlocatie (edit-modus); de parent kent deze al, dus geen emit bij init. */
  readonly initialLocation = input<HdBirthLocation | null>(null);
  readonly locationChange = output<HdBirthLocation | null>();

  protected readonly countries = signal<PlatformCountryResponse[]>([]);
  protected readonly selectedCountry = signal<string>('');
  protected readonly citySuggestions = signal<PlatformCityResponse[]>([]);
  protected readonly cityQuery = signal('');
  readonly value = signal<HdBirthLocation | null>(null);
  readonly error = signal<string | null>(null);
  private cityTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      countryLabel: translate('humanDesign.chart.createDialog.countryLabel'),
      countryPlaceholder: translate('humanDesign.chart.createDialog.countryPlaceholder'),
      countryAria: translate('humanDesign.chart.createDialog.countryAria'),
      countryDropdownAria: translate('humanDesign.chart.createDialog.countryDropdownAria'),
      countryNoResults: translate('humanDesign.chart.createDialog.countryNoResults'),
      countryLoading: translate('humanDesign.chart.createDialog.countryLoading'),
      cityLabel: translate('humanDesign.chart.createDialog.cityLabel'),
      cityPlaceholder: translate('humanDesign.chart.createDialog.cityPlaceholder'),
      cityAria: translate('humanDesign.chart.createDialog.cityAria'),
      locationValidationError: translate('humanDesign.chart.createDialog.locationValidationError'),
    };
  });

  ngOnInit(): void {
    const initial = this.initialLocation();
    if (initial) {
      this.value.set(initial);
      this.selectedCountry.set(initial.countryCode);
      this.cityQuery.set(initial.city);
    }
    // Direct subscribe (geen firstValueFrom/await): zo wordt de landenlijst
    // synchroon gezet zodra de response emit — selectCity kan dan meteen de
    // landnaam resolven zonder op een microtask te wachten.
    this.geocoding.getGeocodingCountries().subscribe(list => {
      this.countries.set(list ?? []);
      this.applyInitialCountry();
    });
  }

  /**
   * Voorinvulling kent soms enkel de landcode, soms enkel een landnaam-hint (uit een
   * "Stad, Land"-place-string); beide richtingen resolven zodra de landenlijst er is.
   * De verrijkte locatie wordt geëmit zodat een latere herberekening de geresolvede
   * landcode meestuurt; de naam-only-detectie blijft intact omdat birthInputsUnchanged
   * landcodes enkel vergelijkt wanneer beide zijden er één hebben.
   */
  private applyInitialCountry(): void {
    const current = this.value();
    if (!current) return;
    let code = current.countryCode;
    let name = current.countryName;
    if (!code && name) {
      // De hint uit de place-string kan een landnaam ("Belgium") óf een landcode ("NL") zijn.
      const lowered = name.toLowerCase();
      code = this.countries().find(c =>
        (c.name ?? '').toLowerCase() === lowered || (c.code ?? '').toLowerCase() === lowered,
      )?.code ?? '';
    }
    if (code && (!name || name.toLowerCase() === code.toLowerCase())) {
      name = this.countries().find(c => c.code === code)?.name ?? name;
    }
    if (!code || (code === current.countryCode && name === current.countryName)) return;
    const enriched: HdBirthLocation = { ...current, countryCode: code, countryName: name };
    this.value.set(enriched);
    this.selectedCountry.set(code);
    this.locationChange.emit(enriched);
  }

  selectCountry(code: string): void {
    this.selectedCountry.set(code);
    this.clearSelection();
  }

  onCityInput(query: string): void {
    this.cityQuery.set(query);
    this.error.set(null);
    if (this.cityTimer) clearTimeout(this.cityTimer);
    if (query.trim().length < 2) { this.citySuggestions.set([]); return; }
    this.cityTimer = setTimeout(() => void this.loadCities(query.trim()), CITY_DEBOUNCE_MS);
  }

  private async loadCities(query: string): Promise<void> {
    const cities = await firstValueFrom(this.geocoding.getGeocodingCities({ country: this.selectedCountry() || null, query }));
    this.citySuggestions.set(cities ?? []);
  }

  selectCity(city: PlatformCityResponse): void {
    const countryName = this.countries().find(c => c.code === (city.countryCode ?? this.selectedCountry()))?.name ?? '';
    const location: HdBirthLocation = {
      place: `${city.name}, ${countryName}`.replace(/, $/, ''),
      city: city.name ?? '',
      countryCode: city.countryCode ?? this.selectedCountry(),
      countryName,
      latitude: city.latitude ?? 0,
      longitude: city.longitude ?? 0,
      timezone: city.timezone ?? '',
    };
    this.value.set(location);
    this.cityQuery.set(location.city);
    this.citySuggestions.set([]);
    this.error.set(null);
    this.locationChange.emit(location);
  }

  /** Vrije tekst (geen selectie) → server-side valideren. */
  async confirmFreeText(place: string): Promise<void> {
    if (this.value()?.city === place) return; // al geselecteerd
    const response = await firstValueFrom(this.geocoding.postGeocodingValidate({ body: { place, country: this.selectedCountry() || null } }));
    if (!response?.valid) {
      this.error.set(
        response?.errorMessage
        || this.text().locationValidationError,
      );
      this.value.set(null);
      this.locationChange.emit(null);
      return;
    }
    const countryName = this.countries().find(c => c.code === this.selectedCountry())?.name ?? '';
    const location: HdBirthLocation = {
      place: response.place ?? place,
      city: place,
      countryCode: this.selectedCountry(),
      countryName,
      latitude: response.latitude ?? 0,
      longitude: response.longitude ?? 0,
      timezone: response.timezone ?? '',
    };
    this.value.set(location);
    this.error.set(null);
    this.locationChange.emit(location);
  }

  private clearSelection(): void {
    this.value.set(null);
    this.cityQuery.set('');
    this.citySuggestions.set([]);
    this.error.set(null);
    this.locationChange.emit(null);
  }
}
